import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faArrowUp,
  faArrowDown,
  faBullhorn,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import debounce from 'lodash.debounce';
import '../../css/JuanIS/JuanISFacultyAnnouncements.css';

function JuanISFacultyAnnouncements({ email }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'startDate',
    direction: 'desc',
  });

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setCurrentPage(1);
      fetchAnnouncements(1, searchValue);
    }, 500),
    [sortConfig, email]
  );

  useEffect(() => {
    fetchAnnouncements(currentPage);
  }, [currentPage, sortConfig, email]);

  useEffect(() => {
    if (searchTerm !== '') {
      debouncedSearch(searchTerm);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  const fetchAnnouncements = async (page = currentPage, searchValue = searchTerm) => {
    try {
      setLoading(true);
      setError(null);

      if (!email) {
        setError('No user email provided. Please log in.');
        setLoading(false);
        return;
      }

      const axiosWithRetry = async (config, retries = 3, delay = 1000) => {
        try {
          const response = await axios(config);
          return response;
        } catch (error) {
          if (retries > 0 && error.response?.status !== 400) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            return axiosWithRetry(config, retries - 1, delay);
          }
          throw error;
        }
      };

      const response = await axiosWithRetry({
        method: 'get',
        url: `${process.env.REACT_APP_API_URL}/api/announcements/faculty`,
        params: {
          page,
          limit: 5,
          search: searchValue,
          sortBy: sortConfig.key,
          sortOrder: sortConfig.direction,
          userEmail: email,
        },
      });

      const validAnnouncements = response.data.announcements.filter(
        (announcement) => announcement.audience === 'Faculty' && announcement.status === 'Active'
      );

      setAnnouncements(validAnnouncements);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || validAnnouncements.length);

      const viewPromises = validAnnouncements.map((announcement) =>
        axiosWithRetry({
          method: 'post',
          url: `${process.env.REACT_APP_API_URL}/api/announcements/view`,
          data: {
            userEmail: email,
            announcementId: announcement._id,
          },
        })
      );
      await Promise.all(viewPromises);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      if (err.response?.status === 400) {
        setError('Invalid request. Please check your input and try again.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please try again later.');
      } else {
        setError('Failed to load announcements. Please check your connection and try again.');
      }
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchAnnouncements(1, '');
  };

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="juanis-faculty-announcements-content-section">
      <h2>Announcements</h2>
      <div className="juanis-faculty-announcements-divider"></div>

      <div className="juanis-faculty-announcements-banner">
        Stay updated with important faculty news and updates.
      </div>

      <div className="juanis-faculty-announcements-controls">
        <div className="juanis-faculty-announcements-search">
          <div className="juanis-faculty-announcements-search-input-container">
            <FontAwesomeIcon icon={faSearch} className="juanis-faculty-announcements-search-icon" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="juanis-faculty-announcements-search-input"
              aria-label="Search announcements"
            />
            {searchTerm && (
              <button
                className="juanis-faculty-announcements-search-clear-button"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </button>
            )}
          </div>
        </div>

        <div className="juanis-faculty-announcements-sort-controls">
          <span className="juanis-faculty-announcements-sort-label">Sort by: </span>
          <button
            onClick={() => requestSort('startDate')}
            className={`juanis-faculty-announcements-sort-button ${sortConfig.key === 'startDate' ? 'active' : ''}`}
            aria-label="Sort by date"
          >
            Date
            {sortConfig.key === 'startDate' && (
              <FontAwesomeIcon
                icon={sortConfig.direction === 'asc' ? faArrowUp : faArrowDown}
                className="juanis-faculty-announcements-sort-icon"
              />
            )}
          </button>
          <button
            onClick={() => requestSort('subject')}
            className={`juanis-faculty-announcements-sort-button ${sortConfig.key === 'subject' ? 'active' : ''}`}
            aria-label="Sort by title"
          >
            Title
            {sortConfig.key === 'subject' && (
              <FontAwesomeIcon
                icon={sortConfig.direction === 'asc' ? faArrowUp : faArrowDown}
                className="juanis-faculty-announcements-sort-icon"
              />
            )}
          </button>
        </div>
      </div>

      {loading && (
        <div className="juanis-faculty-announcements-loading">
          <div className="juanis-faculty-announcements-loading-spinner"></div>
          <p>Loading announcements...</p>
        </div>
      )}

      {error && (
        <div className="juanis-faculty-announcements-error">
          <p>{error}</p>
          <button onClick={() => fetchAnnouncements()} className="juanis-faculty-announcements-retry-button">
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="juanis-faculty-announcements-list">
            {announcements.length > 0 ? (
              announcements.map((announcement) => (
                <div className="juanis-faculty-announcements-item" key={announcement._id}>
                  <div className="juanis-faculty-announcements-header">
                    <div className="juanis-faculty-announcements-uploader">
                      <div className="juanis-faculty-announcements-uploader-icon-container">
                        <FontAwesomeIcon icon={faBullhorn} className="juanis-faculty-announcements-uploader-icon" />
                      </div>
                      <span className="juanis-faculty-announcements-uploader-name">{announcement.announcer}</span>
                    </div>
                    <h3 className="juanis-faculty-announcements-item-title">{announcement.subject}</h3>
                  </div>
                  <p className="juanis-faculty-announcements-item-content">{announcement.content}</p>
                  <div className="juanis-faculty-announcements-footer">
                    <span className="juanis-faculty-announcements-item-date">
                      Posted: {formatDate(announcement.startDate)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="juanis-faculty-announcements-no-announcements">
                <FontAwesomeIcon icon={faBell} className="juanis-faculty-announcements-no-data-icon" />
                <p>No active announcements found</p>
                {searchTerm && (
                  <button className="juanis-faculty-announcements-clear-search-button" onClick={clearSearch}>
                    Clear Search
                  </button>
                )}
              </div>
            )}
          </div>

          {announcements.length > 0 && totalPages > 1 && (
            <div className="juanis-faculty-announcements-pagination">
              <button
                onClick={() => {
                  const newPage = Math.max(currentPage - 1, 1);
                  setCurrentPage(newPage);
                }}
                disabled={currentPage === 1}
                className="juanis-faculty-announcements-pagination-button"
                aria-label="Previous page"
              >
                Back
              </button>

              <div className="juanis-faculty-announcements-pagination-info">
                <span>
                  {currentPage} of {totalPages}
                </span>
              </div>

              <button
                onClick={() => {
                  const newPage = Math.min(currentPage + 1, totalPages);
                  setCurrentPage(newPage);
                }}
                disabled={currentPage === totalPages}
                className="juanis-faculty-announcements-pagination-button"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default JuanISFacultyAnnouncements;